import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart3, 
  Target, 
  Users, 
  FileText, 
  TrendingUp, 
  Shield,
  CheckCircle
} from "lucide-react";

const CHAMPPage = () => {
  const features = [
    {
      icon: BarChart3,
      title: "Integrated Analytics",
      description: "Track programs, services, and activities across multiple communities or locations in one centralized system."
    },
    {
      icon: Target,
      title: "Performance Monitoring",
      description: "Monitor key performance indicators (KPIs), outputs, and outcomes with real-time dashboards."
    },
    {
      icon: TrendingUp,
      title: "Data-Driven Insights",
      description: "Analyze population-level and program-level data to inform strategic decision-making."
    },
    {
      icon: FileText,
      title: "Automated Reporting",
      description: "Generate dashboards, summaries, and reports for leadership, boards, and funders."
    },
    {
      icon: CheckCircle,
      title: "Evaluation Support",
      description: "Support evaluation, quality improvement, and grant reporting requirements seamlessly."
    },
    {
      icon: Shield,
      title: "Secure & Reliable",
      description: "Web-based, secure platform designed specifically for health organizations' data needs."
    }
  ];

  const useCases = [
    "Chronic disease prevention and management programs",
    "Community outreach and education activities",
    "Mobile health and public health nursing services",
    "Grant-funded programs requiring routine reporting and evaluation",
    "Community Health Needs Assessments (CHNAs) and strategic planning"
  ];

  const benefits = [
    "Program effectiveness and accountability",
    "Data-informed decision-making",
    "Funding sustainability and compliance",
    "Transparency and communication with stakeholders",
    "Health equity and measurable community impact"
  ];

  return (
    <div className="flex-1 bg-[#FAF8F3]" data-testid="champ-page">
      {/* Header */}
      <section className="bg-primary-dark text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-block px-4 py-2 bg-primary/20 rounded-full mb-6 border border-primary/30">
            <span className="text-primary text-sm font-semibold">Data Platform</span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold mb-6 font-heading">
            CHAMP
          </h1>
          <p className="text-2xl sm:text-3xl text-gray-200 mb-4 font-heading">
            Community Health Analytics, Monitoring & Performance Platform
          </p>
          <p className="text-lg text-gray-300 max-w-3xl">
            A secure, web-based application designed to help health organizations plan, track, evaluate, and improve programs in one centralized system.
          </p>
        </div>
      </section>

      {/* What CHAMP Does */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-neutral-charcoal mb-4 font-heading">
              What CHAMP Does
            </h2>
            <p className="text-lg text-neutral-gray max-w-3xl mx-auto">
              CHAMP integrates data collection, analytics, performance monitoring, and reporting into a single platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              const colors = ['bg-primary', 'bg-accent-sage', 'bg-accent-sand', 'bg-accent-clay', 'bg-primary', 'bg-accent-sage'];
              return (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 ${colors[index]} rounded-2xl flex items-center justify-center mb-4`}>
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-neutral-charcoal font-heading">
                      {feature.title}
                    </h3>
                    <p className="text-neutral-gray text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Screenshots */}
      <section className="py-16 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-neutral-charcoal mb-6 font-heading">
              Platform Overview
            </h2>
            <p className="text-lg text-neutral-gray max-w-3xl mx-auto">
              See how CHAMP streamlines community health management with intuitive dashboards and powerful analytics
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_a8ffb7c6-de37-44aa-9280-081d48fa4acd/artifacts/xup2h3hu_CHAMP%20Dashboard%205%20Services.png" 
                alt="CHAMP Dashboard showing overview of services and activities"
                className="w-full h-auto"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2 text-primary font-heading">Dashboard Overview</h3>
                <p className="text-sm text-neutral-gray">
                  Real-time insights with interactive filters, service tracking, and location-based analytics
                </p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_a8ffb7c6-de37-44aa-9280-081d48fa4acd/artifacts/z48o6io9_CHAMP%20Activities.png" 
                alt="CHAMP Activities view showing program tracking"
                className="w-full h-auto"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2 text-accent-sage font-heading">Activities Management</h3>
                <p className="text-sm text-neutral-gray">
                  Track and manage all community health activities across divisions, locations, and programs
                </p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_a8ffb7c6-de37-44aa-9280-081d48fa4acd/artifacts/ug3ppas6_CHAMP%20Data%20Entry.png" 
                alt="CHAMP Data Entry form for recording activities"
                className="w-full h-auto"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2 text-accent-sand font-heading">Data Entry</h3>
                <p className="text-sm text-neutral-gray">
                  Streamlined data collection with customizable forms for program activities and participant tracking
                </p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-shadow">
              <img 
                src="https://customer-assets.emergentagent.com/job_a8ffb7c6-de37-44aa-9280-081d48fa4acd/artifacts/gnet6chv_CHAMP%20reports%20and%20exports.png" 
                alt="CHAMP Reports and Export functionality"
                className="w-full h-auto"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2 text-accent-clay font-heading">Reports & Export</h3>
                <p className="text-sm text-neutral-gray">
                  Generate comprehensive reports with CSV/Excel export and evaluation tools for stakeholders
                </p>
              </CardContent>
            </Card>

            <Card className="overflow-hidden hover:shadow-xl transition-shadow md:col-span-2 max-w-2xl mx-auto">
              <img 
                src="https://customer-assets.emergentagent.com/job_a8ffb7c6-de37-44aa-9280-081d48fa4acd/artifacts/3vtfaf7i_CHAMP%20User%20Management.png" 
                alt="CHAMP User Management interface"
                className="w-full h-auto"
              />
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2 text-primary font-heading">User Management</h3>
                <p className="text-sm text-neutral-gray">
                  Role-based access control with administrators, program managers, and staff members
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Built For */}
      <section className="py-16 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-neutral-charcoal mb-6 font-heading">
                Built for Population, Community & Public Health
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                CHAMP is purpose-built for real-world public health and community health work. The platform reflects how public health teams actually operate, prioritizing usability, clarity, and meaningful insights for frontline staff, program managers, and leadership.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Organizations can leverage CHAMP to support community health divisions, public health departments, tribal health organizations, hospitals, and nonprofit programs that need a practical, data-driven way to manage performance, demonstrate impact, and support continuous improvement.
              </p>
            </div>
            <div>
              <Card className="shadow-xl border-l-4 border-primary">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-4 text-primary font-heading">Use Cases</h3>
                  <ul className="space-y-3">
                    {useCases.map((useCase, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <span className="text-primary mt-1 font-bold">•</span>
                        <span className="text-sm">{useCase}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why CHAMP Matters */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-neutral-charcoal mb-6 font-heading">
              Why CHAMP Matters
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Many organizations rely on disconnected spreadsheets, manual reports, and siloed data systems. CHAMP replaces these with a centralized, streamlined solution that strengthens:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {benefits.map((benefit, index) => (
              <Card key={index} className="bg-primary/5 border-primary/20">
                <CardContent className="p-6 text-center">
                  <CheckCircle className="text-primary mx-auto mb-3" size={32} />
                  <p className="text-neutral-charcoal font-semibold">{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Card className="max-w-4xl mx-auto bg-gradient-to-r from-primary/10 to-accent-sage/10 border-none shadow-lg">
              <CardContent className="p-8">
                <p className="text-xl text-neutral-charcoal font-semibold leading-relaxed">
                  CHAMP turns data into actionable insight—helping organizations demonstrate value, improve performance, and improve health outcomes.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.2) 35px, rgba(255,255,255,.2) 70px)'}}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 font-heading">
            Ready to Transform Your Data Management?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Learn how CHAMP can help your organization streamline operations and improve health outcomes.
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-primary hover:bg-gray-100 font-semibold shadow-lg text-lg px-12 py-6"
            >
              Get Started with CHAMP
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CHAMPPage;
